import { useHotkeys } from 'react-hotkeys-hook';

export type ShortcutProps = {
  /** A single combo. Parts are joined with `+`. e.g. `['Meta', 'z']` → `meta+z`. */
  keys?: string[];
  /**
   * Multiple combos that all trigger the same handler — used for cross-platform
   * accelerators like Cmd-Z / Ctrl-Z. Each inner array is one combo. Mutually
   * exclusive with `keys`; if both are passed, `keyCombos` wins.
   */
  keyCombos?: string[][];
  onShortcut?: () => void;
  disabled?: boolean;
  /**
   * If provided and returns `false`, the hook calls neither `preventDefault()`
   * nor `onShortcut()` — letting native handlers (e.g. text-field undo) win.
   */
  shouldHandleEvent?: (event: KeyboardEvent) => boolean;
  onEscKeyDown?: () => void;
};

const buildHotkeyString = (keys: string[] | undefined, keyCombos: string[][] | undefined): string => {
  if (keyCombos && keyCombos.length > 0) {
    return keyCombos
      .map(combo => combo.join('+'))
      .join(', ')
      .toLowerCase();
  }
  return keys?.join('+').toLowerCase() ?? '';
};

const useShortcut = ({
  keys,
  keyCombos,
  onShortcut,
  onEscKeyDown,
  disabled = false,
  shouldHandleEvent,
}: ShortcutProps) => {
  const hotkeyString = buildHotkeyString(keys, keyCombos);

  useHotkeys(
    hotkeyString,
    event => {
      if (shouldHandleEvent && !shouldHandleEvent(event)) return;
      event.preventDefault();
      onShortcut?.();
    },
    {
      enabled: !disabled,
      preventDefault: false,
    },
    [onShortcut, disabled, hotkeyString, shouldHandleEvent],
  );

  useHotkeys(
    'esc',
    () => onEscKeyDown?.(),
    {
      enabled: !disabled && !!onEscKeyDown,
    },
    [onEscKeyDown, disabled],
  );
};

export default useShortcut;
